import { apiClient } from "./client";

// Release 1.6.0 — VIPER Rebalance (O-R74, O-R75). Mirrors SummitOperations.Contracts/ViperRebalance/ViperRebalanceDtos.cs.
// Simulated figures are "predicted"; only a Master Scheduler's signed authorisation changes the plan. Nothing here
// writes to GSS. While planning.viper_rebalance.enabled is off every call answers 404 and the screens hide themselves.

export interface ViperRebalanceStatusDto {
  enabled: boolean;
  canPropose: boolean;
  canAuthorise: boolean;
  allowSelfAuthorisation: boolean;
  proposalExpiryHours: number;
  waitingCount: number;
  waitingForMe: number;
}

export interface LeverDto {
  key: string;
  label: string;
  supported: boolean;
}

export interface WorkcenterOptionDto {
  code: string;
  label: string;
}

export interface WorkOrderOptionDto {
  key: string;
  display: string;
  isLate: boolean;
  slackWorkdays: number;
}

export interface GoalOptionsDto {
  workcenters: WorkcenterOptionDto[];
  topLevelsAtRisk: WorkOrderOptionDto[];
  levers: LeverDto[];
  governedMaxMoves: number;
  bottleneck: string | null;
  emphases: string[];
}

export interface ObjectiveProfileDto {
  goal: string;
  protectedWorkOrders: WorkOrderOptionDto[];
  relieveWorkcenters: string[];
  allowedLevers: string[];
  maxMoves: number;
  emphasis: string;
  /** rules | viper | planner */
  readBy: string;
  notes: string[];
  valueKind: string;
  auditId: string | null;
}

export interface ReadGoalResponseDto {
  profile: ObjectiveProfileDto;
  options: GoalOptionsDto;
}

export interface ObjectiveProfileInput {
  goal: string | null;
  protectedWorkOrders: string[];
  relieveWorkcenters: string[];
  allowedLevers: string[];
  maxMoves: number | null;
  emphasis: string | null;
  readBy: string | null;
}

export interface FiguresDto {
  onTimePercent: number;
  lateOrders: number;
  lateHotOrders: number;
  lateTopLevels: number;
  bottleneckLoadPercent: number;
  overloadedMachineDays: number;
  overtimeHours: number;
  bottleneck: string | null;
  valueKind: string;
}

export interface MoveLineDto {
  id: number;
  kind: string;
  kindLabel: string;
  text: string;
  operationKey: string | null;
  workOrderKey: string | null;
  workOrder: string | null;
}

export interface ChangeRowDto {
  operationKey: string;
  operation: string;
  workOrderKey: string;
  workOrder: string;
  fromWorkcenter: string | null;
  toWorkcenter: string | null;
  fromStart: string;
  toStart: string;
  fromFinish: string;
  toFinish: string;
  moveId: number | null;
  why: string;
}

export interface CandidateDto {
  key: string;
  label: string;
  description: string;
  scenarioId: number | null;
  sameAs: string | null;
  eligible: boolean;
  ineligibleReason: string | null;
  moves: MoveLineDto[];
  before: FiguresDto;
  after: FiguresDto;
  movedOperations: number;
  frozenZoneBreaches: number;
  readinessBreachesAdded: number;
  changeCount: number;
  changes: ChangeRowDto[];
  changesTotal: number;
  stopReason: string;
  valueKind: string;
}

export interface NarrativeDto {
  text: string;
  valueKind: string;
  isAiWritten: boolean;
  auditId: string | null;
  fallbackReason: string | null;
}

export interface RunDto {
  id: number;
  /** queued | running | done | failed */
  state: string;
  goal: string;
  profile: ObjectiveProfileDto;
  createdBy: string;
  createdAtUtc: string;
  finishedAtUtc: string | null;
  candidatesTotal: number;
  candidatesDone: number;
  engineRuns: number;
  progressPercent: number;
  stage: string;
  planHash: string | null;
  stale: boolean;
  whatMoved: string[];
  candidates: CandidateDto[];
  narrative: NarrativeDto | null;
  error: string | null;
  submittedProposalId: number | null;
  valueKind: string;
}

export interface RunSummaryDto {
  id: number;
  state: string;
  goal: string;
  createdAtUtc: string;
  candidates: number;
  submittedProposalId: number | null;
}

export interface ProposalEventDto {
  kind: string;
  actor: string;
  atUtc: string;
  reason: string | null;
  custodyId: string | null;
  selfAuthorised: boolean;
}

export interface ProposalDto {
  id: number;
  runId: number;
  candidateKey: string;
  candidateLabel: string;
  scenarioId: number;
  /** submitted | authorised | rejected | withdrawn | expired */
  state: string;
  stateLabel: string;
  proposedBy: string;
  proposedByUpn: string;
  reason: string;
  submittedAtUtc: string;
  expiresAtUtc: string;
  expired: boolean;
  expiredBecause: string | null;
  stale: boolean;
  whatMoved: string[];
  changesHash: string;
  candidate: CandidateDto;
  previousAfter: FiguresDto | null;
  resimulatedAtUtc: string | null;
  decidedBy: string | null;
  decidedAtUtc: string | null;
  decisionReason: string | null;
  selfAuthorised: boolean;
  custodyId: string | null;
  isMine: boolean;
  canAuthorise: boolean;
  wouldBeSelfAuthorised: boolean;
  authoriseRefusal: string | null;
  profile: ObjectiveProfileDto;
  events: ProposalEventDto[];
  attestation: string;
  valueKind: string;
}

export interface ProposalSummaryDto {
  id: number;
  candidateLabel: string;
  state: string;
  stateLabel: string;
  proposedBy: string;
  submittedAtUtc: string;
  expiresAtUtc: string;
  expired: boolean;
  stale: boolean;
  selfAuthorised: boolean;
  decidedBy: string | null;
  moves: number;
  before: FiguresDto;
  after: FiguresDto;
  valueKind: string;
}

export interface ProposalListDto {
  waiting: ProposalSummaryDto[];
  recent: ProposalSummaryDto[];
}

export interface RebalanceDecisionRequest {
  reason: string;
  confirmSignature: boolean;
  expectedChangesHash?: string | null;
}

export const VIPER_REBALANCE_QK = {
  status: ["viper-rebalance", "status"] as const,
  runs: ["viper-rebalance", "runs"] as const,
  run: (id: number) => ["viper-rebalance", "run", id] as const,
  proposals: ["viper-rebalance", "proposals"] as const,
  proposal: (id: number) => ["viper-rebalance", "proposal", id] as const
};

const BASE = "/planning/viper-rebalance";

export const viperRebalanceApi = {
  status: async (): Promise<ViperRebalanceStatusDto> => (await apiClient.get<ViperRebalanceStatusDto>(`${BASE}/status`)).data,
  readGoal: async (goal: string): Promise<ReadGoalResponseDto> => (await apiClient.post<ReadGoalResponseDto>(`${BASE}/goal/read`, { goal })).data,
  startRun: async (profile: ObjectiveProfileInput): Promise<RunDto> => (await apiClient.post<RunDto>(`${BASE}/runs`, { profile })).data,
  listRuns: async (): Promise<RunSummaryDto[]> => (await apiClient.get<RunSummaryDto[]>(`${BASE}/runs`)).data,
  getRun: async (id: number): Promise<RunDto> => (await apiClient.get<RunDto>(`${BASE}/runs/${id}`)).data,
  setAside: async (id: number): Promise<RunDto> => (await apiClient.post<RunDto>(`${BASE}/runs/${id}/set-aside`, {})).data,
  submit: async (runId: number, candidateKey: string, reason: string): Promise<ProposalDto> =>
    (await apiClient.post<ProposalDto>(`${BASE}/runs/${runId}/submit`, { candidateKey, reason })).data,
  listProposals: async (): Promise<ProposalListDto> => (await apiClient.get<ProposalListDto>(`${BASE}/proposals`)).data,
  getProposal: async (id: number): Promise<ProposalDto> => (await apiClient.get<ProposalDto>(`${BASE}/proposals/${id}`)).data,
  withdraw: async (id: number, reason: string): Promise<ProposalDto> => (await apiClient.post<ProposalDto>(`${BASE}/proposals/${id}/withdraw`, { reason })).data,
  resimulate: async (id: number): Promise<ProposalDto> => (await apiClient.post<ProposalDto>(`${BASE}/proposals/${id}/resimulate`, {})).data,
  /** Signed (AuthorizeRebalance): the signed-in Master Scheduler's own tick is the confirmation; 428 until given. */
  authorise: async (id: number, body: RebalanceDecisionRequest): Promise<ProposalDto> =>
    (await apiClient.post<ProposalDto>(`${BASE}/proposals/${id}/authorise`, body)).data,
  /** Signed (RejectRebalance). */
  reject: async (id: number, body: RebalanceDecisionRequest): Promise<ProposalDto> =>
    (await apiClient.post<ProposalDto>(`${BASE}/proposals/${id}/reject`, body)).data
};

/** The goal chips: each appends a plain phrase the rules reader understands. */
export const GOAL_CHIPS: readonly { id: string; label: string; phrase: string }[] = [
  { id: "protect", label: "Protect the 3 critical top levels", phrase: "protect the 3 critical top levels" },
  { id: "bottleneck", label: "Relieve the bottleneck", phrase: "relieve the bottleneck" },
  { id: "no-overtime", label: "No overtime", phrase: "no overtime" },
  { id: "keep-sequence", label: "Keep the sequence", phrase: "keep the sequence" },
  { id: "least-change", label: "Least change", phrase: "least change" },
  { id: "on-time", label: "Best on-time", phrase: "on time" }
];

export const EMPHASIS_LABELS: Record<string, string> = {
  balanced: "Balanced",
  "on-time": "Best on-time",
  "least-change": "Least change",
  bottleneck: "Relieve the bottleneck"
};
